import React, { useMemo } from 'react';
import { useSearchParams } from 'react-router-dom';
import { Package } from 'lucide-react';
import LeadFormBlock from '../components/blocks/LeadFormBlock';
import Seo from '../components/Seo';
import { useLang } from '../LangContext';
import { usePrices, getBundles } from '../hooks/usePrices';
import { getProduct } from '../catalog';

export default function OffertePage() {
  const { lang, c } = useLang();
  const [params] = useSearchParams();
  const bundleId = params.get('bundle');
  const prices = usePrices();
  const bundles = useMemo(() => getBundles(prices, lang), [prices, lang]);
  const bundle = bundleId && bundles?.items.find((b) => b.id === bundleId);

  // Composition label and pre-fill note for the backend lead.
  const composition = bundle
    ? bundle.products
        .map((p) => {
          const prod = getProduct(lang, p.category, p.slug);
          return prod ? `${p.qty}× ${prod.name}` : null;
        })
        .filter(Boolean)
        .join(', ')
    : null;

  const note = bundle
    ? lang === 'de'
      ? `Interesse am Bundle "${bundle.name}" (${composition}) zum Paketpreis ${bundle.displayPrice}.`
      : `Interesse in pakket "${bundle.name}" (${composition}) voor pakketprijs ${bundle.displayPrice}.`
    : null;

  return (
    <article data-testid="offerte-page" className="relative bg-[#FAF8F2]">
      <Seo
        title={lang === 'de' ? 'Kostenloses Angebot anfragen' : 'Offerte aanvragen'}
        description={lang === 'de'
          ? 'Erhalte ein unverbindliches, persönliches Angebot für deinen Insektenschutz.'
          : 'Ontvang een vrijblijvende, persoonlijke offerte voor uw horren.'}
        path="/offerte"
        lang={lang}
      />
      <div className="pt-24 sm:pt-32" />

      {bundle && (
        <section data-testid="offerte-bundle-banner" className="max-w-[1440px] mx-auto px-5 lg:px-10 pt-6">
          <div className="rounded-[22px] bg-[#1F1D1A] text-white p-6 lg:p-7 flex flex-col md:flex-row md:items-center gap-5 md:gap-8 shadow-[0_30px_60px_-30px_rgba(31,29,26,0.45)]">
            <div className="shrink-0 w-12 h-12 rounded-full bg-[#F26B1A] inline-flex items-center justify-center">
              <Package className="w-5 h-5 text-white" />
            </div>
            <div className="flex-1">
              <p className="text-[11px] uppercase tracking-[0.22em] text-[#F26B1A] font-semibold mb-1.5">
                {lang === 'de' ? 'Dein gewähltes Paket' : 'Uw gekozen pakket'}
              </p>
              <h2 className="font-display text-[22px] lg:text-[28px] leading-tight">{bundle.name}</h2>
              <p className="text-white/75 text-[13.5px] mt-1.5">{composition}</p>
            </div>
            <div className="md:text-right">
              <p className="font-display text-[30px] lg:text-[36px] leading-none">{bundle.displayPrice}</p>
              {bundle.save > 0 && (
                <p className="text-[12.5px] text-[#F26B1A] mt-1.5 font-semibold">
                  {bundles.saveLabel} {bundle.displaySave} · −{bundle.savePct}%
                </p>
              )}
            </div>
          </div>
        </section>
      )}

      <LeadFormBlock
        {...c.leadForm}
        lang={lang}
        rhythm="grand"
        source={bundle ? `bundle-${bundle.id}` : 'offerte-page'}
        extraNote={note}
      />
    </article>
  );
}
